import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Medal, RefreshCw, Instagram, ShieldAlert, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

// Toggle blur screen on (true) or off (false)
const IS_BLURRED = false;

const PODIUM_STYLE = [
  { rank: 1, ring: "border-[#FF9933]", bg: "bg-[#FF9933]", h: "h-28", label: "1st" },
  { rank: 2, ring: "border-slate-400", bg: "bg-slate-400", h: "h-20", label: "2nd" },
  { rank: 3, ring: "border-amber-700", bg: "bg-amber-700", h: "h-16", label: "3rd" },
];

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/public/leaderboard");
      setData(data.leaderboard || []);
    } catch { /* keep old data */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const top3 = (data || []).slice(0, 3);
  const rest = (data || []).slice(3);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Blurred Leaderboard Container */}
      <div 
        className={`max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16 transition-all duration-500 ${
          IS_BLURRED ? "filter blur-md pointer-events-none select-none" : ""
        }`} 
        data-testid="leaderboard-page"
      >
        <div className="text-center">
          <div className="text-xs font-bold tracking-[0.3em] text-[#FF9933] uppercase">Live Standings</div>
          <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tighter text-slate-900 mt-2">Leaderboard</h1>
          <p className="text-sm text-slate-500 mt-2">Updates automatically every 10 seconds. Only public display names are shown.</p>
        </div>

        {top3.length > 0 && (
          <div className="mt-12 flex items-end justify-center gap-4 md:gap-8">
            {[PODIUM_STYLE[1], PODIUM_STYLE[0], PODIUM_STYLE[2]].map((style, idx) => {
              const entry = top3[style.rank - 1];
              if (!entry) return <div key={style.rank} className="w-28" />;
              return (
                <motion.div key={style.rank} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.15 }}
                  className="flex flex-col items-center" data-testid={`podium-${style.rank}`}>
                  <div className={`relative w-16 h-16 md:w-20 md:h-20 rounded-full border-4 ${style.ring} bg-slate-50 flex items-center justify-center font-heading font-black text-xl text-slate-700 overflow-hidden`}>
                    {entry.avatar ? <img src={entry.avatar} alt="" className="w-full h-full object-cover" /> : entry.display_name.slice(0, 2).toUpperCase()}
                    {style.rank === 1 && <Crown className="absolute -top-1 -right-1 w-5 h-5 text-[#FF9933]" />}
                  </div>
                  <div className="mt-3 text-center">
                    <div className="font-heading font-bold text-slate-900 text-sm md:text-base max-w-[110px] truncate">{entry.display_name}</div>
                    <div className="font-timer font-bold text-[#FF9933] text-lg">{entry.score}</div>
                  </div>
                  <div className={`mt-2 w-24 md:w-28 ${style.h} ${style.bg} rounded-t-xl flex items-start justify-center pt-2`}>
                    <span className="text-white/90 font-timer font-bold text-xl">{style.label}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-12 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-12 gap-2 px-5 py-3.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">
            <div className="col-span-1">Rank</div>
            <div className="col-span-5 md:col-span-4">Player</div>
            <div className="col-span-2 text-right">Score</div>
            <div className="col-span-2 text-right hidden md:block">Correct</div>
            <div className="col-span-2 text-right hidden md:block">Time</div>
            <div className="col-span-4 md:col-span-1 text-right">Cats</div>
          </div>
          {loading && <div className="p-10 text-center text-slate-400 text-sm font-semibold">Loading standings…</div>}
          {!loading && rest.length === 0 && top3.length === 0 && (
            <div className="p-12 text-center" data-testid="leaderboard-empty">
              <Medal className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="font-heading font-bold text-slate-600">No scores yet</div>
              <p className="text-sm text-slate-400 mt-1">Be the first on the board when the competition begins.</p>
            </div>
          )}
          {rest.map((e, i) => (
            <motion.div key={e.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
              data-testid={`leaderboard-row-${e.rank}`}
              className="grid grid-cols-12 gap-2 px-5 py-3.5 border-b border-slate-100 items-center hover:bg-slate-50 transition-colors">
              <div className="col-span-1 font-timer font-bold text-slate-500">#{e.rank}</div>
              <div className="col-span-5 md:col-span-4 flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#003366]/10 text-[#003366] flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden">
                  {e.avatar ? <img src={e.avatar} alt="" className="w-full h-full object-cover" /> : e.display_name.slice(0, 2).toUpperCase()}
                </div>
                <span className="font-semibold text-slate-800 text-sm truncate">{e.display_name}</span>
              </div>
              <div className="col-span-2 text-right font-timer font-bold text-[#FF9933]">{e.score}</div>
              <div className="col-span-2 text-right text-sm text-slate-500 hidden md:block">{e.correct}</div>
              <div className="col-span-2 text-right text-sm text-slate-500 hidden md:block">{(e.time_ms / 1000).toFixed(1)}s</div>
              <div className="col-span-4 md:col-span-1 text-right text-sm text-slate-500">{e.categories}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <button onClick={load} data-testid="leaderboard-refresh" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#FF9933] transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh now
          </button>
        </div>
      </div>

      {/* Overlay Announcement Modal */}
      {IS_BLURRED && (
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="max-w-md w-full bg-white rounded-3xl p-6 md:p-8 shadow-2xl border border-slate-100 text-center"
          >
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-[#FF9933] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-6 h-6" />
            </div>

            <span className="inline-block px-3 py-1 bg-amber-100 text-[#FF9933] text-xs font-bold uppercase tracking-wider rounded-full mb-3">
              Calculating Results
            </span>

            <h2 className="text-2xl font-black font-heading text-slate-900">
              Scores are being verified!
            </h2>
            
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Results are currently under calculation and official winners will be announced live on our Instagram at <span className="font-bold text-slate-800">10:00 PM</span>.
            </p>

            {/* Fair Play Notice */}
            <div className="mt-5 p-3.5 bg-rose-50 border border-rose-100 rounded-xl text-left flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
              <p className="text-xs text-rose-700 leading-normal">
                <strong className="font-semibold">Fair Play Policy:</strong> Any cheating or rule-breaking detected by our system incurs negative points. Final winners will be determined strictly on system fair-play logs.
              </p>
            </div>

            {/* Instagram Button */}
            <a
              href="https://lostdrkc.in/instagram"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] hover:opacity-95 text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-md active:scale-[0.99]"
            >
              <Instagram className="w-5 h-5" />
              <span>Check on Instagram</span>
            </a>
          </motion.div>
        </div>
      )}
    </div>
  );
}